import {
  getAtomBalance,
  getBalanceHint,
  isEquationBalanced,
  isSimplestCoefficientRatio,
  parseEquation,
  type AtomBalance,
} from './chemicalEquation';
import { reviewed, type DataSource, type ReviewStatus } from './energyCalorimetry';
import { FUELS_SOURCES } from './fuelsSustainability';

export type ProductionRoute = 'bioethanol' | 'biodiesel' | 'biogas' | 'hydrogen' | 'fossil';

export const PRODUCTION_ROUTE_LABELS: Record<ProductionRoute, string> = {
  bioethanol: 'Bioethanol',
  biodiesel: 'Biodiesel',
  biogas: 'Biogas',
  hydrogen: 'Hydrogen',
  fossil: 'Fossil feedstock',
};

export interface ProductionSpecies {
  /** ASCII formula read by the shared equation parser, e.g. C2H5OH. */
  formula: string;
  name: string;
  /** State is omitted when it depends on the process temperature. */
  state?: 'aq' | 'g' | 'l' | 's';
}

export interface ProductionCase {
  id: string;
  name: string;
  route: ProductionRoute;
  reactants: ProductionSpecies[];
  products: ProductionSpecies[];
  conditions: string;
  note: string;
  sources: DataSource[];
  reviewStatus: ReviewStatus;
}

/** Coefficients a learner can enter for one species. */
export const MIN_COEFFICIENT = 1;
export const MAX_COEFFICIENT = 20;

function species(
  formula: string,
  name: string,
  state?: ProductionSpecies['state'],
): ProductionSpecies {
  return { formula, name, ...(state ? { state } : {}) };
}

function production(entry: Omit<ProductionCase, 'sources' | 'reviewStatus'>): ProductionCase {
  return { ...entry, ...reviewed([FUELS_SOURCES.processes, FUELS_SOURCES.iupac]) };
}

export const PRODUCTION_CASES: ProductionCase[] = [
  production({
    id: 'fermentation-glucose',
    name: 'Fermentation of glucose',
    route: 'bioethanol',
    reactants: [species('C6H12O6', 'glucose', 'aq')],
    products: [species('C2H5OH', 'ethanol', 'aq'), species('CO2', 'carbon dioxide', 'g')],
    conditions: 'Yeast enzymes, no oxygen (anaerobic), warm water bath of about 30–40 °C.',
    note: 'Ethanol is distilled from the mixture. One third of the glucose carbon leaves as CO₂ during fermentation.',
  }),
  production({
    id: 'fermentation-sucrose',
    name: 'Fermentation of sucrose (overall)',
    route: 'bioethanol',
    reactants: [species('C12H22O11', 'sucrose', 'aq'), species('H2O', 'water', 'l')],
    products: [species('C2H5OH', 'ethanol', 'aq'), species('CO2', 'carbon dioxide', 'g')],
    conditions: 'Yeast first hydrolyses sucrose to glucose and fructose, then ferments them; anaerobic, about 30–40 °C.',
    note: 'Sugar cane and molasses supply sucrose. Water appears as a reactant because hydrolysis comes first.',
  }),
  production({
    id: 'maltose-hydrolysis',
    name: 'Hydrolysis of maltose from grain starch',
    route: 'bioethanol',
    reactants: [species('C12H22O11', 'maltose', 'aq'), species('H2O', 'water', 'l')],
    products: [species('C6H12O6', 'glucose', 'aq')],
    conditions: 'Enzymes (amylases, then maltase) break starch down to glucose before fermentation.',
    note: 'Maltose and sucrose share the formula C₁₂H₂₂O₁₁; they are isomers with different structures.',
  }),
  production({
    id: 'ethene-hydration',
    name: 'Hydration of ethene',
    route: 'fossil',
    reactants: [species('C2H4', 'ethene', 'g'), species('H2O', 'steam', 'g')],
    products: [species('C2H5OH', 'ethanol', 'g')],
    conditions: 'Phosphoric acid catalyst on silica, about 300 °C and 6–7 MPa.',
    note: 'The ethanol molecule is identical to bioethanol, but the ethene comes from cracking oil or gas fractions.',
  }),
  production({
    id: 'cracking-decane',
    name: 'Catalytic cracking of decane',
    route: 'fossil',
    reactants: [species('C10H22', 'decane', 'g')],
    products: [species('C8H18', 'octane', 'g'), species('C2H4', 'ethene', 'g')],
    conditions: 'Zeolite catalyst, about 450–550 °C.',
    note: 'Cracking turns long, less useful alkanes into petrol-range alkanes and alkenes such as ethene.',
  }),
  production({
    id: 'biodiesel-triolein',
    name: 'Transesterification of triolein (biodiesel)',
    route: 'biodiesel',
    reactants: [species('C57H104O6', 'triolein (a vegetable oil triglyceride)'), species('CH3OH', 'methanol')],
    products: [species('C19H36O2', 'methyl oleate (biodiesel)'), species('C3H5(OH)3', 'glycerol')],
    conditions: 'NaOH or KOH catalyst, about 50–65 °C, excess methanol.',
    note: 'Each triglyceride has three ester groups, so it makes three biodiesel molecules and one glycerol.',
  }),
  production({
    id: 'biodiesel-tripalmitin',
    name: 'Transesterification of tripalmitin (biodiesel)',
    route: 'biodiesel',
    reactants: [species('C51H98O6', 'tripalmitin (a palm oil triglyceride)'), species('CH3OH', 'methanol')],
    products: [species('C17H34O2', 'methyl palmitate (biodiesel)'), species('C3H5(OH)3', 'glycerol')],
    conditions: 'NaOH or KOH catalyst, about 50–65 °C, excess methanol.',
    note: 'Glycerol is a by-product. Separating and using it improves the economics and waste profile.',
  }),
  production({
    id: 'biogas-glucose',
    name: 'Anaerobic digestion of glucose (overall)',
    route: 'biogas',
    reactants: [species('C6H12O6', 'glucose', 'aq')],
    products: [species('CH4', 'methane', 'g'), species('CO2', 'carbon dioxide', 'g')],
    conditions: 'Mixed bacteria and archaea in a sealed digester, no oxygen, about 35 °C.',
    note: 'Biogas is roughly half to two thirds CH₄; the rest is mainly CO₂.',
  }),
  production({
    id: 'biogas-acetic-acid',
    name: 'Methane from ethanoic acid',
    route: 'biogas',
    reactants: [species('CH3COOH', 'ethanoic acid', 'aq')],
    products: [species('CH4', 'methane', 'g'), species('CO2', 'carbon dioxide', 'g')],
    conditions: 'Methane-producing archaea in an anaerobic digester.',
    note: 'Ethanoic acid is an intermediate when microbes break down biomass.',
  }),
  production({
    id: 'steam-reforming',
    name: 'Steam reforming of methane',
    route: 'hydrogen',
    reactants: [species('CH4', 'methane', 'g'), species('H2O', 'steam', 'g')],
    products: [species('CO', 'carbon monoxide', 'g'), species('H2', 'hydrogen', 'g')],
    conditions: 'Nickel catalyst, about 700–1000 °C.',
    note: 'This is how most hydrogen is made today. The CO is then shifted to CO₂.',
  }),
  production({
    id: 'water-gas-shift',
    name: 'Water–gas shift',
    route: 'hydrogen',
    reactants: [species('CO', 'carbon monoxide', 'g'), species('H2O', 'steam', 'g')],
    products: [species('CO2', 'carbon dioxide', 'g'), species('H2', 'hydrogen', 'g')],
    conditions: 'Metal oxide catalysts, about 200–450 °C.',
    note: 'Adding this step to steam reforming gives CH₄ + 2H₂O → CO₂ + 4H₂ overall. The CO₂ is released at the plant.',
  }),
  production({
    id: 'electrolysis-water',
    name: 'Electrolysis of water',
    route: 'hydrogen',
    reactants: [species('H2O', 'water', 'l')],
    products: [species('H2', 'hydrogen', 'g'), species('O2', 'oxygen', 'g')],
    conditions: 'Direct current through water containing an electrolyte.',
    note: 'No carbon is involved, so the hydrogen is low-emission only if the electricity is from solar, wind or another low-emission source.',
  }),
  production({
    id: 'methanol-synthesis',
    name: 'Methanol from synthesis gas',
    route: 'fossil',
    reactants: [species('CO', 'carbon monoxide', 'g'), species('H2', 'hydrogen', 'g')],
    products: [species('CH3OH', 'methanol', 'g')],
    conditions: 'Copper/zinc oxide catalyst, about 250 °C and 5–10 MPa.',
    note: 'The CO and H₂ usually come from natural gas. The same synthesis can use gas made from biomass.',
  }),
];

/**
 * Reviewed smallest whole-number coefficients, reactants then products. The learner starts
 * from the skeleton equation (all coefficients 1).
 */
export const PRODUCTION_BALANCED: Record<string, number[]> = {
  'fermentation-glucose': [1, 2, 2],
  'fermentation-sucrose': [1, 1, 4, 4],
  'maltose-hydrolysis': [1, 1, 2],
  'ethene-hydration': [1, 1, 1],
  'cracking-decane': [1, 1, 1],
  'biodiesel-triolein': [1, 3, 3, 1],
  'biodiesel-tripalmitin': [1, 3, 3, 1],
  'biogas-glucose': [1, 3, 3],
  'biogas-acetic-acid': [1, 1, 1],
  'steam-reforming': [1, 1, 1, 3],
  'water-gas-shift': [1, 1, 1, 1],
  'electrolysis-water': [2, 2, 1],
  'methanol-synthesis': [1, 2, 1],
};

export function productionSpecies(entry: ProductionCase): ProductionSpecies[] {
  return [...entry.reactants, ...entry.products];
}

/** The reviewed balanced coefficients, in reactant-then-product order. */
export function balancedCoefficients(entry: ProductionCase): number[] | null {
  const values = PRODUCTION_BALANCED[entry.id];
  if (!values || values.length !== productionSpecies(entry).length) return null;
  return values;
}

/** Skeleton coefficients (all 1) for the learner to start from. */
export function skeletonCoefficients(entry: ProductionCase): number[] {
  return productionSpecies(entry).map(() => 1);
}

function term(item: ProductionSpecies, coefficient: number): string {
  return `${coefficient === 1 ? '' : coefficient}${item.formula}${item.state ? `(${item.state})` : ''}`;
}

/** ASCII equation for given coefficients, readable by the shared parser. */
export function productionEquation(entry: ProductionCase, coefficients: number[]): string {
  const left = entry.reactants.map((item, index) => term(item, coefficients[index]));
  const right = entry.products.map((item, index) =>
    term(item, coefficients[entry.reactants.length + index]),
  );
  return `${left.join(' + ')} -> ${right.join(' + ')}`;
}

export type BalanceStatus = 'balanced' | 'not-simplest' | 'unbalanced' | 'invalid';

export interface BalanceCheck {
  status: BalanceStatus;
  feedback: string;
  atoms: AtomBalance[];
}

export function isValidCoefficient(value: number): boolean {
  return Number.isInteger(value) && value >= MIN_COEFFICIENT && value <= MAX_COEFFICIENT;
}

/** Atom tally for the learner's current coefficients, or [] when any coefficient is invalid. */
export function atomTally(entry: ProductionCase, coefficients: number[]): AtomBalance[] {
  if (coefficients.length !== productionSpecies(entry).length) return [];
  if (!coefficients.every(isValidCoefficient)) return [];
  const parsed = parseEquation(productionEquation(entry, coefficients));
  return parsed.equation ? getAtomBalance(parsed.equation) : [];
}

export function checkProductionBalance(entry: ProductionCase, coefficients: number[]): BalanceCheck {
  const count = productionSpecies(entry).length;
  if (coefficients.length !== count || !coefficients.every(isValidCoefficient)) {
    return {
      status: 'invalid',
      feedback: `Give every substance a whole-number coefficient from ${MIN_COEFFICIENT} to ${MAX_COEFFICIENT}. Never change a formula subscript.`,
      atoms: [],
    };
  }
  const parsed = parseEquation(productionEquation(entry, coefficients));
  if (!parsed.equation) {
    return { status: 'invalid', feedback: parsed.error?.message ?? 'Equation not recognised.', atoms: [] };
  }
  const atoms = getAtomBalance(parsed.equation);
  if (!isEquationBalanced(parsed.equation)) {
    return { status: 'unbalanced', feedback: getBalanceHint(parsed.equation), atoms };
  }
  if (!isSimplestCoefficientRatio(parsed.equation)) {
    return {
      status: 'not-simplest',
      feedback:
        'Every atom balances, but all the coefficients share a common factor. Divide them all by it to give the smallest whole-number ratio.',
      atoms,
    };
  }
  return {
    status: 'balanced',
    feedback: `Balanced. ${entry.note}`,
    atoms,
  };
}
